import find from 'lodash/find';
import React, { FC, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import FieldToggle from 'src/presentation/components/forms/FieldToggle';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import generateId from 'src/lib/generateId';
import { getPossibleParents } from 'src/lib/hierarchy';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import { nameWithAncestry } from './hierarchy';
import Switch from 'src/presentation/components/forms/Switch';
import topicsResource, { HierarchicalTopic } from 'src/actions/topics/topicsResource';
import { useResourceValues } from 'src/presentation/hooks/useResource';

const dataAidPrefix = 'admin-topics-';
const parentIdAttr = 'parentId';

const TopicEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { values: topics } = useResourceValues(topicsResource);
  const { add, update, validate } = topicsResource.effects;
  const { resetErrors } = topicsResource.actions;
  const [parentDropdownEnabled, setParentDropdownEnabled] = useState(item ? !!item.parentId : false);

  const saveHandler = useOnSave(parentDropdownEnabled);

  const formContext = useForm({
    add,
    defaultItem: { disabled: false, name: '' },
    errors,
    item,
    resetErrors,
    root,
    update,
    validate,
    onSave: saveHandler,
  });

  const { workingState, onClose, onSave, isNew } = formContext;
  const dropdownOptions = useDropdownOptions(topics, item);
  const selectedDropdownOption = find(dropdownOptions, o => o.value === workingState[parentIdAttr]);

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Add' : 'Update'} Topic</H1>

      <Body data-aid={`${dataAidPrefix}editor`}>
        <Section>
          <Row>
            <Input
              data-aid={`${dataAidPrefix}editor-name-input`}
              attr="name"
              autoFocus
              label="Topic Name"
              placeholder="Enter name"
            />
            <Switch data-aid={`${dataAidPrefix}editor-disabled-toggle`} attr="disabled" label="Active" flipValue />
          </Row>
          <Row>
            <FieldToggle
              attr={parentIdAttr}
              dataAid="admin-topics-toggle-parent-dropdown"
              doNotExpandContent
              label="Nest topic under"
              onChange={(isEnabled, currentVal) => {
                if (isEnabled) {
                  setParentDropdownEnabled(true);
                  return currentVal;
                }
                setParentDropdownEnabled(false);
                return '';
              }}
            >
              {isEnabled => (
                <Dropdown
                  attr={parentIdAttr}
                  data-aid={`${dataAidPrefix}parent-dropdown`}
                  disabled={!isEnabled}
                  options={dropdownOptions}
                  placeholder="Select parent topic"
                  searchable
                  text={selectedDropdownOption ? selectedDropdownOption.label : undefined}
                  value={workingState[parentIdAttr]}
                />
              )}
            </FieldToggle>
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-save`} onClick={onSave}>
          {isNew ? 'Add Topic' : 'Save'}
        </Button>
      </Footer>
    </Form>
  );
};

function useOnSave(parentDropdownEnabled: boolean) {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, validate, workingStateRef }) => {
    dispatch(resetErrors());

    const currentWorkingState = workingStateRef.current;
    const dataToValidate = { ...currentWorkingState, parentDropdownEnabled };
    if (validate && !dispatch(validate(dataToValidate))) {
      return;
    }

    const getCustomToastMsg = () => {
      const workingStateName = currentWorkingState.name;
      const ARCHIVED_MESSAGE = `We archived ${workingStateName}. Your topics list just got a little lighter.`;
      const UNARCHIVED_MESSAGE = `We unarchived ${workingStateName}.`;
      const UPDATED_MESSAGE = `We updated the topic ${workingStateName} for you.`;
      const ADDED_MESSAGE = `We added the topic ${workingStateName} for you.`;
      if (isNew) return ADDED_MESSAGE;
      if (item.disabled !== currentWorkingState.disabled) {
        return currentWorkingState.disabled ? ARCHIVED_MESSAGE : UNARCHIVED_MESSAGE;
      }
      return UPDATED_MESSAGE;
    };

    const customToastMsg = getCustomToastMsg();

    isNew
      ? dispatch(add({}, { id: generateId(), ...currentWorkingState }, { customToastMsg }))
      : dispatch(update({ id: item.id }, currentWorkingState, { customToastMsg }));
  };
}

function useDropdownOptions(topics: HierarchicalTopic[], topic?: HierarchicalTopic) {
  return useMemo(() => {
    const possibleParents = topic ? getPossibleParents(topics, topic) : topics;

    return possibleParents
      .filter(topic => !topic.disabled)
      .map(topic => {
        const fullName = nameWithAncestry(topic);

        return { label: fullName, text: fullName, value: topic.id };
      });
  }, [topics, topic]);
}

export default TopicEditor;
